import CTNFilterBar, {
	CTNFilterButton,
	CTNFilterCalendarRange,
	CTNFilterSearch,
} from '@ctn/ui/components/ctn-filter-bar';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableRef } from '@ctn/ui/types/ctn-table';
import { format } from 'date-fns';
import {
	forwardRef,
	type ReactElement,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type ISolicitudDocumentacionPayload,
	type ISolicitudDocumentacionResponse,
	postSubsanacionesSolicitudesDocumentacionListado,
} from '@/services/tramite.service';

import { FORMAT_DATE } from '@/constants/date.constants';
import { PAGE_SIZE } from '@/constants/table.constants';
import { ESTADO_SUBSANACION } from '@/constants/tramite.constants';
import { cleanObj } from '@/helpers/objects';
import { type ISolicitudDocumentacion } from '@/interfaces/model/don.interface';
import { type IPagination } from '@/interfaces/model/generic.interface';


import SolicitudesDocumentacionListado from '../SolicitudesDocumentacionListado';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_PAGINATION, payloadDefault } from './config';
import { getColumnsAtendidas, getColumnsPendiente } from '../SolicitudesDocumentacionListado/columns';

type SolicitudPendiente = {
	readonly idEstado: typeof ESTADO_SUBSANACION.PENDIENTE;
	readonly onViewDetail?: never;
	readonly onSendDocumentacion: (rowData: ISolicitudDocumentacion) => void;
};

type SolicitudSubsanada = {
	readonly idEstado: typeof ESTADO_SUBSANACION.SUBSANADA;
	readonly onSendDocumentacion?: never;
	readonly onViewDetail: (rowData: ISolicitudDocumentacion) => void;
};

type SolicitudesDocumentacionFilterProps = SolicitudPendiente | SolicitudSubsanada;

export type SolicitudesDocumentacionFilterRef = {
	reload: () => void;
};

const SolicitudesDocumentacionFilter = forwardRef<
SolicitudesDocumentacionFilterRef,
SolicitudesDocumentacionFilterProps
>(({ onViewDetail, idEstado, onSendDocumentacion }, ref): ReactElement => {
	const filterBarRef = useRef<CTNFilterBarRef>(null);

	const tableRef = useRef<CTNTableRef<ISolicitudDocumentacion>>(null);
	const { loading, triggerFetch } = useApiData({ loading: ['listado'] as const });
	const [dataTable, setDataTable] = useState<ISolicitudDocumentacionResponse>({ totalResults: 10, listado: [] });
	const [pagination, setPagination] = useState<IPagination>(PAYLOAD_PAGINATION);

	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues,
	});

	const search = watch('search');
	const tipoBusqueda = search?.[0]?.key ?? defaultValues.tipoBusqueda;
	const fechaAlta = watch('fechaAlta');
	const location = useLocation();
	const { navigateData, setNavigateData } = usePageContext();
	const [isReady, setIsReady] = useState(false);
	const isInitialLoad = useRef(true);


	const formatData = ({
		tipoBusqueda,
		busqueda,
		fechaAlta = [],
		...data
	}: IPagination & FilterFormData): IPagination & Partial<ISolicitudDocumentacionPayload> => {
		const [fechaDesde, fechaHasta] = fechaAlta;
		const fechaDesdeStr = fechaDesde ? format(fechaDesde, FORMAT_DATE.BASIC_DASH) : undefined;
		const fechaHastaStr = fechaHasta ? format(fechaHasta, FORMAT_DATE.BASIC_DASH) : undefined;
		return {
			...data,
			[tipoBusqueda]: busqueda,
			listaIdsEstados: [idEstado],
			fechaDesde: fechaDesdeStr,
			fechaHasta: fechaHastaStr,
		};
	};

	const getData = async (payload: IPagination & Partial<ISolicitudDocumentacionPayload>) => {
		const { data } = await triggerFetch({
			fetch: postSubsanacionesSolicitudesDocumentacionListado,
			payload: { ...payloadDefault(), ...cleanObj(payload) },
			name: 'listado',
		});
		if (data) {
			setDataTable(data);
		}
	};

	useEffect(() => {				
		if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			getData(formatData({ ...PAYLOAD_PAGINATION, tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, busqueda: search?.[0]?.search ?? '', fechaAlta }));
			setIsReady(true);
			return;
		}

		const {data} = navigateData.filter;
		if (!data) {
			getData(formatData({ ...PAYLOAD_PAGINATION, tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, busqueda: search?.[0]?.search ?? '', fechaAlta }));
			setIsReady(true);
			return;
		}
		reset({
			...defaultValues,
			search: [{ key: data.tipoBusqueda ?? defaultValues.tipoBusqueda, search: data.numeroTramite }],
			fechaAlta: [data.fechaDesde, data.fechaHasta],
		});
		filterBarRef.current?.addFilters({
			search: data.numeroTramite ? [{ key: data.tipoBusqueda ?? defaultValues.tipoBusqueda, search: data.numeroTramite ?? '' }] : [],
			fechaAlta: [data.fechaDesde ? new Date(data.fechaDesde) : undefined, data.fechaHasta ? new Date(data.fechaHasta) : undefined],
		});
		const { filter, ...resto } = navigateData ?? {};
		setNavigateData(resto);
		setIsReady(true);
		isInitialLoad.current = false;
	}, []); 

	const onPageChange = (start: number) => {
		let newPagination = {
			start: PAGE_SIZE.OPERACIONES * (start - 1),
			limit: PAGE_SIZE.OPERACIONES,
		};

		if (start === 1) {
			newPagination = {
				start: 0,
				limit: PAGE_SIZE.OPERACIONES,
			};
		}
		getData(
			formatData({
				...newPagination,
				tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, 
				busqueda: search?.[0]?.search ?? '',
				fechaAlta,
			}),
		);
		setPagination(newPagination);
	};

	useImperativeHandle(ref, () => ({
		reload: () =>
			getData(
				formatData({
					...pagination,
					tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, 
					busqueda: search?.[0]?.search ?? '',
					fechaAlta,
				}),
			),
	}));

	const columns = useMemo(() => {
		const columnsMap = {
			[ESTADO_SUBSANACION.PENDIENTE]: getColumnsPendiente({ onSendDocumentacion }),
			[ESTADO_SUBSANACION.SUBSANADA]: getColumnsAtendidas({ onViewDetail }),
		};
		return columnsMap[idEstado] ?? getColumnsPendiente({ onSendDocumentacion });
	}, [idEstado]);

	useEffect(() => {
		if (!isReady) return;
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		const payload = formatData({
			...pagination,
			tipoBusqueda: search?.[0]?.key ?? defaultValues.tipoBusqueda, 
			busqueda: search?.[0]?.search ?? '',
			fechaAlta,
		});
		getData(payload);
		setNavigateData({ filter: { urls: [location.pathname], data: payload }});
	}, [search, tipoBusqueda, fechaAlta?.[0], fechaAlta?.[1], isReady]);

	return (
		<section className="u-mt-24">
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" placeholder="Nº solicitud" label="Buscar"
					tag="Búsqueda"
					options={[{ label: 'Nº solicitud', value: 'numeroTramite' }]} />
				<CTNFilterCalendarRange
					label="Fecha de alta"
					placeholder="Fecha de alta"
					name="fechaAlta"
					tag="Fecha de alta"
					tagFormat="dd/MM/yyyy"
					locale="es"
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
				<div className="c-pnp-box__body">
					<SolicitudesDocumentacionListado
						tableRef={tableRef}
						isLoading={loading.listado}
						columns={columns}
						data={dataTable.listado}
						totalData={dataTable.totalResults}
						onPageChange={onPageChange}
					/>
				</div>
			</div>
		</section>
	);
});

SolicitudesDocumentacionFilter.displayName = 'SolicitudesDocumentacionFilter';

export default SolicitudesDocumentacionFilter;
